"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, MapPin, Phone, Mail } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactUs() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  return <section id="contact-us" className="py-24 px-6 bg-light-background text-foreground-light">
      <div className="max-w-7xl mx-auto">
        <div ref={ref} className="grid lg:grid-cols-2 gap-16 transition-all duration-200 ease-out" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(10px)"
      }}>
          {}
          <div className="flex flex-col justify-center space-y-8">
            <div>
              <h2 className="text-4xl font-bold mb-4 text-foreground-light">
                Get In Touch
              </h2>
              <p className="text-muted text-lg">
                Contact us today to discuss your freight transportation needs. We're here to provide reliable logistics solutions.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex size-12 items-center justify-center rounded-md bg-primary/10 shrink-0">
                  <MapPin className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1 text-foreground-light">Address</h3>
                  <p className="text-muted">37431 Deville St, Palmdale CA 93552</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex size-12 items-center justify-center rounded-md bg-primary/10 shrink-0">
                  <Phone className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1 text-foreground-light">Phone</h3>
                  <p className="text-muted">(661) 200-9865</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex size-12 items-center justify-center rounded-md bg-primary/10 shrink-0">
                  <Mail className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1 text-foreground-light">Email</h3>
                  <p className="text-muted">info@management101llc.com</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="flex items-center justify-center">
            <div className="w-full max-w-xl">
              <FormProvider className="bg-background border border-border rounded-lg p-8 shadow-soft" formId="contact-form-hero" sectionName="contact-us" defaultValues={{
              name: "",
              company: "",
              email: "",
              phone: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2,
                maxLength: 50
              },
              company: {
                maxLength: 100
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              message: {
                required: true,
                minLength: 10,
                maxLength: 1000
              }
            }} submitText="Send Message" successMessage="Thank you for contacting us! We'll get back to you soon.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary-light border border-primary rounded-lg text-center">
        <p className="text-foreground-light text-lg font-medium mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="transition-all duration-120 hover:scale-102">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 50,
          message: "Name must not exceed 50 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" rules={{
        maxLength: {
          value: 100,
          message: "Company name must not exceed 100 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Company</FormLabel>
              <FormControl>
                <Input placeholder="Your Company (Optional)" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[\d\s\-\+\(\)]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(123) 456-7890 (Optional)" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        },
        maxLength: {
          value: 1000,
          message: "Message must not exceed 1000 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your freight transportation needs..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full transition-all duration-120 hover:scale-102">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : "Send Message"}
        </Button>
      </div>
    </Form>;
}